import { Component, DestroyRef, ElementRef, HostBinding, HostListener, inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { CommonModule, NgClass } from '@angular/common';
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import {
	AppIdEnum,
	ENVIRONMENT,
	LibSharedMsgboxComponent,
	MsgboxEnum,
	SPINNER_MESSAGE,
	SPINNER_SILENT
} from '@lib/shared';
import { LorenzFormControl } from '../../lorenz-form-control';
import { LorenzFormGroup } from '../../lorenz-form-group';
import { LibFormValidityDotComponent } from '../../components/validity-dot/lib-form-validity-dot.component';
import { LorenzFormComponentDisplayOptions } from '../../interfaces/lorenz-form-component-options.interface';
import { getLorenzFormLayoutEnumString, LorenzFormLayoutEnum } from '../../enums/lorenz-form-layout.enum';
import { ProcessStatusEnum } from '../../enums/process-status.enum';
import { debounceTime, finalize, timeout } from 'rxjs';
import { ContractDocumentListCoordinatorService } from './contract-document-list-coordinator.service';

type ContractDocumentReviewStatus = 'UNGEPRUEFT' | 'GEPRUEFT' | 'NACHGEFORDERT';
type ContractDocumentSlot = 'active' | 'pending';

interface ContractDocumentItem {
	id: number;
	slot?: ContractDocumentSlot;
	type: string;
	category?: 'A' | 'B' | null;
	review_status?: ContractDocumentReviewStatus;
	review_gate_seen_at?: string | null;
	reviewed_at?: string | null;
	requested_at?: string | null;
	requested_reason?: string | null;
	file_available?: boolean;
	original_name: string;
	expires_at: string | null;
	is_expired: boolean;
	is_invalid_for_contract_start?: boolean;
	invalid_for_contract_start_reason?: string | null;
	contract_reference_date?: string | null;
	expires_cleanup_at?: string | null;
	linked_at?: string | null;
	uploaded_at?: string | null;
	downloaded_at?: string | null;
	is_latest?: boolean;
	is_pending_partner_upload?: boolean;
	is_selected_for_contract?: boolean;
	is_explicitly_selected_for_contract?: boolean;
	is_selectable_for_contract?: boolean;
	is_latest_upload?: boolean;
	invalid_for_contracts?: Array<{
		contract_id: number;
		contract_start: string | null;
		contract_end?: string | null;
		label?: string | null;
		contract_link?: string | null;
	}>;
	invalid_for_open_contracts_count?: number;
}

interface PartnerHiddenDocumentState {
	mode: 'hidden' | 'restored';
	document: ContractDocumentItem;
}

interface PartnerUploadFeedbackState {
	message: string;
	created_at?: number;
}

@Component({
	selector: 'lib-form-control-document-upload',
	imports: [CommonModule, ReactiveFormsModule, NgClass, LibFormValidityDotComponent, MatButtonModule, MatIconModule, MatTooltipModule, TranslatePipe],
	templateUrl: './lib-form-control-document-upload.component.html',
	styleUrl: './lib-form-control-document-upload.component.scss'
})
export class LibFormControlDocumentUploadComponent implements OnInit, OnChanges, OnDestroy {
	private readonly destroyRef = inject(DestroyRef);
	private readonly dialog = inject(MatDialog);
	private readonly httpClient = inject(HttpClient);
	private readonly contractDocumentListCoordinator = inject(ContractDocumentListCoordinatorService);
	private readonly environment = inject(ENVIRONMENT);
	private readonly categoryADocumentTypes = ['aufenthaltstitel'];
	private readonly maxUploadFiles = 10;
	private readonly maxTotalUploadSizeKb = 20480;
	@ViewChild('fileInput') private fileInputRef?: ElementRef<HTMLInputElement>;

	@Input({ required: true, alias: 'control' }) lorenzFormControl!: LorenzFormControl;
	@Input({ required: false, alias: 'options' }) lorenzFormComponentDisplayOptions: LorenzFormComponentDisplayOptions = {};

	@HostBinding('attr.class') hostCssClass = '';

	lorenzFormGroup!: LorenzFormGroup;

	allDocuments: ContractDocumentItem[] = [];
	documents: ContractDocumentItem[] = [];
	expiredDocuments: ContractDocumentItem[] = [];
	errorMessage: string | null = null;
	successMessage: string | null = null;
	revalidationMessage: string | null = null;
	isLoading = false;
	isUploading = false;
	private queuedFile: File | null = null;
	private pendingFilesBuffer: File[] = [];

	expiresAtControl = new FormControl<string | null>(null);
	expiryEditControl = new FormControl<string | null>(null);
	contractDocumentSelectionControl = new FormControl<number | null>(null);
	private editingExpiryDocumentKey: string | null = null;
	private revalidateControls: { key: string; control: LorenzFormControl }[] = [];
	private revalidateBaseline = new Map<string, any>();
	private baselineInitialized = false;
	private lastDocumentId: number | null = null;
	needsRevalidation = false;
	private successTimeoutId: number | null = null;
	private loadRequestToken = 0;
	private revalidationInitialized = false;
	private pendingReloadAfterCurrentLoad = false;
	private readonly preparePendingUploadForSave = (): boolean => this.prepareDeferredUploadForSave();
	private readonly applyUploadedItemsAfterSave = (items: ContractDocumentItem[]): void => this.applyUploadedItemsFromSave(items);
	isSavingExpiry = false;
	isSelectingContractDocument = false;

	ngOnInit(): void {
		this.initControlContext();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['lorenzFormControl'] && !changes['lorenzFormControl'].firstChange) {
			const previousControl = changes['lorenzFormControl'].previousValue as LorenzFormControl | null;
			this.unregisterPendingUploadPreparation(previousControl);
			this.resetStateForControlChange();
			this.initControlContext();
		}
	}

	ngOnDestroy(): void {
		this.unregisterPendingUploadPreparation(this.lorenzFormControl);
	}

	@HostListener('window:lorenz-health-certificate-refreshed')
	onHealthCertificateRefreshed(): void {
		if (this.isHealthCertificateType) {
			this.loadDocuments(true);
		}
	}

	get hasLayoutFloatingLabels(): boolean {
		return this.lorenzFormComponentDisplayOptions.layout === LorenzFormLayoutEnum.FLOATING_LABELS;
	}

	get showErrorMessages(): boolean {
		return this.lorenzFormControl.showErrors() && (this.lorenzFormComponentDisplayOptions.showErrorMessages ?? true);
	}

	get visibleSuccessMessage(): string | null {
		return this.lorenzFormControl.payload?.documentUploadStatusMessage ?? this.successMessage;
	}

	get visibleErrorMessage(): string | null {
		return this.lorenzFormControl.payload?.documentUploadErrorMessage ?? this.errorMessage;
	}

	get documentType(): string | null {
		return this.lorenzFormControl.payload?.documentType ?? null;
	}

	get isHealthCertificateType(): boolean {
		return this.documentType === 'gesundheitszeugnis';
	}

	get partnerDocumentRenewalLink(): { url: string; text: string } | null {
		const link = this.lorenzFormControl.payload?.documentRenewalLink;
		if (!this.isContractApp || !link?.url || this.hasValidPendingUpload) {
			return null;
		}

		const documents = this.uploadedDocuments;
		const needsRenewal = (doc: ContractDocumentItem): boolean =>
			doc.is_expired === true
			|| this.isInvalidForContractStart(doc)
			|| this.isDateBeforeContractValidityEnd(doc.expires_at);
		const hasValidDocument = documents.some((doc) => this.isFileAvailable(doc) && !needsRenewal(doc));
		return !hasValidDocument && documents.some(needsRenewal) ? link : null;
	}

	get allowDownload(): boolean {
		if (this.isContractApp) {
			return false;
		}
		if (this.lorenzFormControl.payload?.documentAllowDownload !== undefined) {
			return this.lorenzFormControl.payload.documentAllowDownload;
		}
		return this.lorenzFormControl.payload?.appId !== AppIdEnum.CONTRACT;
	}

	get allowPreview(): boolean {
		if (this.lorenzFormControl.payload?.documentAllowPreview !== undefined) {
			return this.lorenzFormControl.payload.documentAllowPreview;
		}
		if (this.isContractApp) {
			return true;
		}
		return this.allowDownload;
	}

	get allowDelete(): boolean {
		return this.lorenzFormControl.payload?.documentAllowDelete ?? true;
	}

	get allowUpload(): boolean {
		return this.lorenzFormControl.payload?.documentAllowUpload ?? true;
	}

	get isContractApp(): boolean {
		return this.lorenzFormControl.payload?.appId === AppIdEnum.CONTRACT;
	}

	get isPersonDocumentsApp(): boolean {
		return this.documentContextMode === 'person';
	}

	get allowMultipleDocuments(): boolean {
		return this.isPersonDocumentsApp;
	}

	get showTextActionButtons(): boolean {
		return this.isContractApp;
	}

	get deferUpload(): boolean {
		const configuredValue = this.lorenzFormControl.payload?.documentDeferUpload ?? this.isContractApp;
		if (!configuredValue) {
			return false;
		}

		if (this.isBackendContractEditApp) {
			return [ProcessStatusEnum.NEW, ProcessStatusEnum.REVIEW].includes(this.currentProcessStatus ?? ProcessStatusEnum.UNKNOWN);
		}

		return true;
	}

	get expiryRequired(): boolean {
		return this.lorenzFormControl.payload?.documentExpiryRequired ?? false;
	}

	get expiryOptional(): boolean {
		return this.lorenzFormControl.payload?.documentExpiryOptional ?? false;
	}

	get hasSelectedFileForUpload(): boolean {
		return !!this.queuedFile || (this.isMultiPageEnabled && this.hasBufferedMultiPageFiles);
	}

	get showExpiryInput(): boolean {
		return this.hasSelectedFileForUpload && (this.expiryRequired || this.expiryOptional);
	}

	get showExpiryMeta(): boolean {
		return this.expiryRequired || this.expiryOptional;
	}

	get showDownloadedMeta(): boolean {
		return !this.isContractApp;
	}

	get isExpiryMissing(): boolean {
		return this.expiryRequired && !this.expiresAtControl.value;
	}

	get showPartnerDeferredSaveHint(): boolean {
		return this.deferUpload && this.allowUpload && !this.hasFrozenReviewedDocument;
	}

	get showPartnerUploadActionButton(): boolean {
		if (!this.isContractApp) {
			return true;
		}
		if (!this.hasSelectedFileForUpload) {
			return false;
		}
		if (this.showExpiryInput && !this.expiresAtControl.value) {
			return false;
		}
		return true;
	}

	get isPartnerUploadBlockedByContractStart(): boolean {
		return this.isContractApp && this.isSelectedExpiryInvalidForContractStart;
	}

	get shouldHighlightPartnerExpiryInput(): boolean {
		return this.isContractApp && this.showExpiryInput && !this.expiresAtControl.value;
	}

	get contractValidityDate(): string | null {
		const rootGroup = (this.lorenzFormControl?.getRoot?.() as LorenzFormGroup | null) ?? this.lorenzFormGroup ?? null;
		const contractEnd = String(rootGroup?.get('veranstaltung.vertragsende')?.value ?? '').trim();
		if (contractEnd !== '') {
			return contractEnd;
		}

		const contractStart = String(rootGroup?.get('veranstaltung.vertragsstart')?.value ?? '').trim();
		return contractStart !== '' ? contractStart : null;
	}

	get contractValidityDisplayDate(): string | null {
		return this.formatDateForDisplay(this.contractValidityDate);
	}

	get isSelectedExpiryInvalidForContractStart(): boolean {
		return this.isDateBeforeContractValidityEnd(this.expiresAtControl.value ?? null);
	}

	get hasValidPendingUpload(): boolean {
		return this.hasPendingUpload && !this.isDateBeforeContractValidityEnd(this.pendingExpiresAt);
	}

	get contractValidityInvalidHint(): string {
		const contractValidityDate = this.contractValidityDisplayDate;
		return contractValidityDate
			? `Ablaufdatum liegt vor dem Vertragsende (${contractValidityDate}). Bitte lade ein für den gesamten Vertragszeitraum gültiges Dokument hoch.`
			: 'Das Dokument ist nicht für den gesamten Vertragszeitraum gültig.';
	}

	get acceptTypes(): string {
		return this.lorenzFormControl.payload?.documentAccept ?? '.pdf,.jpg,.jpeg,.png';
	}

	get hasCredentials(): boolean {
		if (this.documentContextMode === 'person') {
			return !!this.personKey;
		}
		return !!this.lorenzFormControl.payload?.code && !!this.lorenzFormControl.payload?.pin;
	}

	get documentContextMode(): 'contract' | 'person' {
		return this.lorenzFormControl.payload?.documentContextMode === 'person' ? 'person' : 'contract';
	}

	get personKey(): string | null {
		const value = this.lorenzFormControl.payload?.personKey;
		if (value === null || value === undefined) {
			return null;
		}
		const trimmed = String(value).trim();
		return trimmed !== '' ? trimmed : null;
	}

	get hasValidDocument(): boolean {
		if (this.allowMultipleDocuments) {
			return false;
		}

		return this.controlValueDocuments.length > 0
			|| this.expiredDocuments.some((doc) => this.isDocumentFrozenInCurrentContract(doc))
			|| this.hasValidPendingUpload;
	}

	get hasExistingPartnerDocument(): boolean {
		if (!this.isContractApp) {
			return false;
		}

		return [...this.displayDocuments, ...this.visibleExpiredDocuments]
			.some((doc) =>
				doc.is_selected_for_contract === true
				|| this.isPendingPartnerUpload(doc)
				|| (!doc.is_expired && !this.isInvalidForContractStart(doc))
			);
	}

	get hasDocumentBlockingUpload(): boolean {
		return this.hasValidDocument || this.hasExistingPartnerDocument;
	}

	get hasFrozenReviewedDocument(): boolean {
		return this.displayDocuments.some((doc) => this.isDocumentFrozenInCurrentContract(doc))
			|| this.expiredDocuments.some((doc) => this.isDocumentFrozenInCurrentContract(doc));
	}

	get hasPendingUpload(): boolean {
		return !!this.lorenzFormControl.payload?.documentPendingFile || this.pendingFiles.length > 0;
	}

	get hasPendingMultiPageUpload(): boolean {
		return this.pendingFiles.length > 0;
	}

	get pendingFiles(): File[] {
		const files = this.lorenzFormControl.payload?.documentPendingFiles;
		return Array.isArray(files) ? files.filter((item) => item instanceof File) : [];
	}

	get hasStoredDocument(): boolean {
		return this.displayDocuments.length > 0;
	}

	get selectableContractDocuments(): ContractDocumentItem[] {
		return this.displayDocuments.filter((doc) => doc.is_selectable_for_contract === true);
	}

	get documentCards(): ContractDocumentItem[] {
		if (!this.showContractDocumentSelection) {
			return this.displayDocuments;
		}

		return this.displayDocuments.filter((doc) => doc.is_selectable_for_contract !== true);
	}

	get hasSelectedContractDocument(): boolean {
		return this.displayDocuments.some((doc) => doc.is_selected_for_contract === true);
	}

	get showContractDocumentSelection(): boolean {
		return this.isBackendContractEditApp
			&& [ProcessStatusEnum.NEW, ProcessStatusEnum.REVIEW].includes(this.currentProcessStatus ?? ProcessStatusEnum.UNKNOWN)
			&& !this.hasSelectedContractDocument
			&& this.displayDocuments.every((doc) => doc.is_selectable_for_contract === true)
			&& this.selectableContractDocuments.length > 0;
	}

	get hasPendingPartnerReplacement(): boolean {
		return this.displayDocuments.some((doc) => this.isPendingPartnerUpload(doc));
	}

	get pendingFileName(): string | null {
		if (this.pendingFiles.length > 0) {
			return this.formatFileNameList(this.pendingFiles.map((file) => file.name));
		}
		const file = this.lorenzFormControl.payload?.documentPendingFile;
		return file ? this.formatDisplayFileName(file.name) : null;
	}

	get pendingMultiPageHint(): string | null {
		if (this.pendingFiles.length <= 0) {
			return null;
		}
		return `${this.pendingFiles.length} Dateien ausgewählt. Zusammenführung erfolgt beim Absenden.`;
	}

	get bufferedMultiPageFileNames(): string | null {
		if (this.pendingFilesBuffer.length <= 0) {
			return null;
		}

		return this.formatFileNameList(this.pendingFilesBuffer.map((file) => file.name));
	}

	get pendingExpiresAt(): string | null {
		return this.lorenzFormControl.payload?.documentPendingExpiresAt ?? null;
	}

	get queuedFileName(): string | null {
		return this.queuedFile ? this.formatDisplayFileName(this.queuedFile.name) : null;
	}

	get hasQueuedFile(): boolean {
		return !!this.queuedFile;
	}

	get bufferedMultiPageCount(): number {
		return this.pendingFilesBuffer.length;
	}

	get hasBufferedMultiPageFiles(): boolean {
		return this.pendingFilesBuffer.length > 0;
	}

	get isMultiPageEnabled(): boolean {
		const explicitFlag = this.lorenzFormControl.payload?.documentMultiPageEnabled;
		if (explicitFlag !== undefined) {
			return explicitFlag;
		}
		return this.isContractApp || this.isBackendContractEditApp || this.isBackendDocumentsApp;
	}

	get allowedUploadFormatsLabel(): string {
		const labels = this.acceptTypes
			.split(',')
			.map((type) => type.trim())
			.filter((type) => type !== '')
			.map((type) => type.replace(/^\./, '').toUpperCase());

		return Array.from(new Set(labels)).join(', ');
	}

	get maxTotalUploadSizeLabel(): string {
		const megaBytes = this.maxTotalUploadSizeKb / 1024;
		if (Number.isInteger(megaBytes)) {
			return `${megaBytes} MB`;
		}
		return `${megaBytes.toFixed(1).replace('.', ',')} MB`;
	}

	get uploadedDocuments(): ContractDocumentItem[] {
		const items = this.lorenzFormControl.payload?.documentUploadedItems;
		return Array.isArray(items) ? (items as ContractDocumentItem[]) : [];
	}

	get displayDocuments(): ContractDocumentItem[] {
		const uploadedActiveDocuments = this.filterDocumentsForCurrentApp(this.uploadedDocuments).filter((item) => !item.is_expired);
		if (this.documents.length > 0) {
			return this.documents;
		}
		return uploadedActiveDocuments;
	}

	get usableDisplayDocuments(): ContractDocumentItem[] {
		return this.displayDocuments.filter((doc) => !this.isInvalidForContractStart(doc));
	}

	get controlValueDocuments(): ContractDocumentItem[] {
		const usableDocuments = !this.isHealthCertificateType
			? this.usableDisplayDocuments
			: [...this.displayDocuments, ...this.expiredDocuments];

		if (this.isBackendContractEditApp) {
			return usableDocuments.filter((doc) =>
				doc.is_selected_for_contract === true
				|| doc.is_pending_partner_upload === true
				|| doc.is_selectable_for_contract !== true
			);
		}

		return usableDocuments;
	}

	get hasDocumentManagementWorkLock(): boolean {
		const rootGroup = (this.lorenzFormControl?.getRoot?.() as LorenzFormGroup | null) ?? this.lorenzFormGroup ?? null;
		return !!rootGroup?.payload?.documentManagementLockActive;
	}

	get visibleExpiredDocuments(): ContractDocumentItem[] {
		return this.expiredDocuments;
	}

	get hasHealthCertificateRefreshAfterContractStart(): boolean {
		if (!this.isHealthCertificateType) {
			return false;
		}

		const refreshControl = (this.lorenzFormControl.parent as LorenzFormGroup | null)?.get('gesundheitszeugnis_auffrischung_dokument');
		const refreshDocuments = Array.isArray(refreshControl?.value) ? refreshControl.value : [];

		return refreshDocuments.some((document: ContractDocumentItem) => this.isDateOnOrAfterContractValidityEnd(document?.expires_at));
	}

	get restoreCandidate(): ContractDocumentItem | null {
		if (!this.isContractApp) {
			return null;
		}

		const state = this.getPartnerHiddenDocumentState();
		if (!state || state.mode !== 'hidden') {
			return null;
		}

		return state.document;
	}

	get canRestoreDocument(): boolean {
		return !!this.restoreCandidate && !this.isBlocked;
	}

	get isBlocked(): boolean {
		if (this.isContractApp && (this.currentProcessStatus ?? ProcessStatusEnum.UNKNOWN) !== ProcessStatusEnum.NEW) {
			return true;
		}
		if (this.hasDocumentManagementWorkLock) {
			return true;
		}
		return this.lorenzFormControl.isBlocked() && !this.allowSignedCategoryBManagement;
	}

	get isBackendContractEditApp(): boolean {
		return this.lorenzFormControl.payload?.appId === AppIdEnum.BACKEND_CONTRACT_EDIT;
	}

	get isBackendDocumentsApp(): boolean {
		return this.lorenzFormControl.payload?.appId === AppIdEnum.BACKEND_DOCUMENTS;
	}

	get isBackendManagementApp(): boolean {
		return this.isBackendContractEditApp || this.isBackendDocumentsApp;
	}

	get isCategoryBType(): boolean {
		const type = (this.documentType ?? '').toLowerCase();
		if (!type) {
			return true;
		}
		return !this.categoryADocumentTypes.includes(type);
	}

	get currentProcessStatus(): ProcessStatusEnum | null {
		const rootGroup = (this.lorenzFormControl?.getRoot?.() as LorenzFormGroup | null) ?? this.lorenzFormGroup ?? null;
		const control = rootGroup?.get('_lorenz.status');
		return (control?.value as ProcessStatusEnum) ?? null;
	}

	get allowSignedCategoryBManagement(): boolean {
		if (!this.isBackendContractEditApp || !this.isCategoryBType) {
			return false;
		}

		return [
			ProcessStatusEnum.YOUSIGN_LORENZ,
			ProcessStatusEnum.YOUSIGN_PARTNER,
			ProcessStatusEnum.SIGNED
		].includes(this.currentProcessStatus ?? ProcessStatusEnum.UNKNOWN);
	}

	get canReviewDocuments(): boolean {
		return this.isBackendManagementApp;
	}

	get apiContext(): 'partner' | 'backend' {
		if (this.documentContextMode === 'person') {
			return 'backend';
		}
		return this.isContractApp ? 'partner' : 'backend';
	}

	get documentsApiBasePath(): string {
		if (this.documentContextMode === 'person') {
			return '/api/v1/person-documents';
		}
		if (this.isBackendContractEditApp) {
			return '/api/v1/admin/contracts/documents';
		}
		return '/api/v1/contracts/documents';
	}

	get revalidateTriggers(): string[] {
		return this.lorenzFormControl.payload?.documentRevalidateOn ?? [];
	}

	get shouldUseDocumentApi(): boolean {
		return !!this.documentType;
	}

	private apiUrl(path: string): string {
		return this.environment.apiServer + `${this.documentsApiBasePath}/${path}`;
	}

	private buildPayload(extra: Record<string, any> = {}): Record<string, any> {
		if (this.documentContextMode === 'person') {
			return {
				person_key: this.personKey,
				include_open_contract_usage: true,
				...extra
			};
		}

		return {
			code: this.lorenzFormControl.payload?.code,
			pin: this.lorenzFormControl.payload?.pin,
			context: this.apiContext,
			...extra
		};
	}

	private buildUploadFormData(file: File): FormData {
		const formData = new FormData();
		if (this.documentContextMode === 'person') {
			formData.append('person_key', this.personKey ?? '');
		} else {
			formData.append('code', this.lorenzFormControl.payload?.code ?? '');
			formData.append('pin', this.lorenzFormControl.payload?.pin ?? '');
			formData.append('context', this.apiContext);
		}

		formData.append('type', this.documentType ?? '');
		formData.append('file', file);

		if (this.expiresAtControl.value) {
			formData.append('expires_at', this.expiresAtControl.value);
		}

		return formData;
	}

	private buildUploadFormDataFiles(files: File[]): FormData {
		const formData = new FormData();
		if (this.documentContextMode === 'person') {
			formData.append('person_key', this.personKey ?? '');
		} else {
			formData.append('code', this.lorenzFormControl.payload?.code ?? '');
			formData.append('pin', this.lorenzFormControl.payload?.pin ?? '');
			formData.append('context', this.apiContext);
		}

		formData.append('type', this.documentType ?? '');
		if (files.length > 1) {
			formData.append('merge_to_pdf', '1');
		}
		files.forEach((file) => formData.append('files[]', file));

		if (this.expiresAtControl.value) {
			formData.append('expires_at', this.expiresAtControl.value);
		}

		return formData;
	}

	canDeleteDocument(doc: ContractDocumentItem): boolean {
		if (!this.allowDelete) {
			return false;
		}
		if (!this.isFileAvailable(doc)) {
			return false;
		}
		if (!this.hasCredentials) {
			return false;
		}
		if (this.isContractApp && doc.is_selected_for_contract !== true) {
			return false;
		}
		if (this.isDocumentFrozenInCurrentContract(doc)) {
			return false;
		}
		if (
			this.isContractApp
			&& this.isHealthCertificateType
			&& (doc.is_expired || this.documentReviewStatus(doc) === 'GEPRUEFT')
		) {
			return false;
		}
		if (this.isBackendDocumentsApp) {
			return true;
		}
		if (this.isBackendContractEditApp && !doc.is_selected_for_contract) {
			return false;
		}
		if (this.documentReviewStatus(doc) === 'GEPRUEFT' && !this.isReviewedDeleteAllowedInCurrentStatus()) {
			return false;
		}
		if (!this.canManageDocumentInCurrentState(doc)) {
			return false;
		}
		if (this.isContractApp && this.documentSlot(doc) === 'active') {
			return true;
		}
		if (this.isBackendContractEditApp) {
			return true;
		}
		return true;
	}

	canEditDocumentExpiry(doc: ContractDocumentItem): boolean {
		if (!this.isBackendManagementApp || !this.showExpiryMeta) {
			return false;
		}
		if (this.isPendingPartnerUpload(doc)) {
			return false;
		}
		if (!this.isFileAvailable(doc) || !this.hasCredentials || doc.is_expired) {
			return false;
		}
		if (this.documentReviewStatus(doc) === 'GEPRUEFT') {
			return false;
		}
		return this.canManageDocumentInCurrentState(doc);
	}

	isEditingDocumentExpiry(doc: ContractDocumentItem): boolean {
		return this.editingExpiryDocumentKey === this.getDocumentIdentity(doc);
	}

	canPreviewDocument(doc: ContractDocumentItem): boolean {
		if (!this.allowPreview) {
			return false;
		}
		return this.hasCredentials && this.isFileAvailable(doc);
	}

	canDownloadDocument(doc: ContractDocumentItem): boolean {
		if (!this.allowDownload) {
			return false;
		}
		return this.hasCredentials && this.isFileAvailable(doc);
	}

	canMarkReviewStatus(doc: ContractDocumentItem): boolean {
		if (!this.canReviewDocuments) {
			return false;
		}
		if (this.hasFrozenReviewedDocument) {
			return false;
		}
		if (doc.is_expired) {
			return false;
		}
		if (!this.isFileAvailable(doc)) {
			return false;
		}
		if (!this.canManageDocumentInCurrentState(doc)) {
			return false;
		}
		if (this.isDocumentRequested(doc)) {
			return false;
		}
		if (!this.isReviewGateOpen(doc)) {
			return false;
		}
		return this.documentReviewStatus(doc) === 'UNGEPRUEFT';
	}

	canRejectDocument(doc: ContractDocumentItem): boolean {
		// Rejecting removes the uploaded person document and is therefore a
		// document-management (master delete) action, not a contract action.
		return this.isBackendDocumentsApp && this.canMakeReplacementDecision(doc);
	}

	canAcceptDocument(doc: ContractDocumentItem): boolean {
		return this.canMakeReplacementDecision(doc);
	}

	canSelectDocumentForContract(doc: ContractDocumentItem): boolean {
		if (!this.showContractDocumentSelection || !this.hasCredentials || !this.documentType) {
			return false;
		}
		if (doc.is_selected_for_contract === true) {
			return false;
		}
		if (doc.is_selectable_for_contract !== true) {
			return false;
		}
		return this.canManageDocumentInCurrentState(doc);
	}

	canResetDocumentSelection(doc: ContractDocumentItem): boolean {
		return this.isBackendContractEditApp
			&& this.hasCredentials
			&& !!this.documentType
			&& doc.is_explicitly_selected_for_contract === true
			&& this.canManageDocumentInCurrentState(doc);
	}

	canSetCurrentDocument(doc: ContractDocumentItem): boolean {
		if (!this.isBackendDocumentsApp || !this.hasCredentials || !this.documentType) {
			return false;
		}
		if (this.isLatestDocument(doc) || !this.isFileAvailable(doc) || doc.is_expired) {
			return false;
		}
		if (this.documentReviewStatus(doc) !== 'GEPRUEFT') {
			return false;
		}
		return this.canManageDocumentInCurrentState(doc);
	}

	private canMakeReplacementDecision(doc: ContractDocumentItem): boolean {
		if (!this.canReviewDocuments) {
			return false;
		}
		if (
			this.hasFrozenReviewedDocument
			&& !(
				this.allowSignedCategoryBManagement
				&& !this.isHealthCertificateType
				&& this.documentCategory(doc) === 'B'
			)
		) {
			return false;
		}
		if (doc.is_expired) {
			return false;
		}
		if (!this.isBackendManagementApp || !this.isPendingPartnerUpload(doc)) {
			return false;
		}
		if (!this.isFileAvailable(doc)) {
			return false;
		}
		if (!this.canManageDocumentInCurrentState(doc)) {
			return false;
		}
		if (this.isDocumentRequested(doc)) {
			return false;
		}
		return this.documentReviewStatus(doc) === 'UNGEPRUEFT';
	}

	isReviewGateOpen(doc: ContractDocumentItem): boolean {
		return !!doc.review_gate_seen_at;
	}

	isDocumentRequested(doc: ContractDocumentItem): boolean {
		return (doc.review_status ?? 'UNGEPRUEFT') === 'NACHGEFORDERT';
	}

	documentReviewStatus(doc: ContractDocumentItem): ContractDocumentReviewStatus {
		return doc.review_status ?? 'UNGEPRUEFT';
	}

	documentSlot(doc: ContractDocumentItem): ContractDocumentSlot {
		return doc.slot ?? 'active';
	}

	isInvalidForContractStart(doc: ContractDocumentItem): boolean {
		return doc.is_invalid_for_contract_start === true;
	}

	getInvalidForContractStartMessage(doc: ContractDocumentItem): string {
		return doc.invalid_for_contract_start_reason ?? 'Dokument ist nicht für den gesamten Vertragszeitraum gültig. Bitte neues Dokument hochladen.';
	}

	getContractStartStatusSummary(doc: ContractDocumentItem): string {
		const contractEnd = this.formatDateForDisplay(doc.contract_reference_date ?? null);
		return contractEnd
			? `Nicht bis zum Vertragsende ${contractEnd} gültig. Bitte neues Dokument hochladen.`
			: 'Nicht für den gesamten Vertragszeitraum gültig. Bitte neues Dokument hochladen.';
	}

	getOpenContractsStatusSummary(doc: ContractDocumentItem): string | null {
		const label = this.getInvalidContractsLabel(doc);
		if (!label) {
			return null;
		}
		return `Betroffene Verträge: ${label}`;
	}

	formatDateForDisplay(value: string | null | undefined): string | null {
		if (!value) {
			return null;
		}

		const trimmed = String(value).trim();
		if (trimmed === '') {
			return null;
		}

		const match = /^(\d{4})-(\d{2})-(\d{2})(?:[T\s].*)?$/.exec(trimmed);
		if (!match) {
			return trimmed;
		}

		return `${match[3]}.${match[2]}.${match[1]}`;
	}

	getUploadedAtDisplay(doc: ContractDocumentItem): string | null {
		return this.formatDateForDisplay(doc.uploaded_at ?? doc.linked_at ?? null);
	}

	isLatestDocument(doc: ContractDocumentItem): boolean {
		return doc.is_latest === true;
	}

	isPendingPartnerUpload(doc: ContractDocumentItem): boolean {
		return doc.is_pending_partner_upload === true || this.documentSlot(doc) === 'pending';
	}

	isSelectedForContract(doc: ContractDocumentItem): boolean {
		return doc.is_selected_for_contract === true;
	}

	hasInvalidOpenContracts(doc: ContractDocumentItem): boolean {
		return Array.isArray(doc.invalid_for_contracts) && doc.invalid_for_contracts.length > 0;
	}

	getInvalidContracts(doc: ContractDocumentItem): Array<{
		contract_id: number;
		label: string;
		contract_period_display: string;
		contract_link: string | null;
	}> {
		if (!this.hasInvalidOpenContracts(doc)) {
			return [];
		}

		return doc.invalid_for_contracts!.map((contract) => {
			const start = this.formatDateForDisplay(contract.contract_start ?? null);
			const end = this.formatDateForDisplay(contract.contract_end ?? null);
			const period = start && end && start !== end ? `${start}–${end}` : (end ?? start ?? 'ohne Vertragszeitraum');

			return {
				contract_id: contract.contract_id,
				label: String(contract.label ?? '').trim() || `Vertrag ${contract.contract_id}`,
				contract_period_display: period,
				contract_link: this.normalizeContractLink(contract.contract_link ?? null)
			};
		});
	}

	getInvalidContractsLabel(doc: ContractDocumentItem): string | null {
		if (!this.hasInvalidOpenContracts(doc)) {
			return null;
		}

		return doc.invalid_for_contracts!
			.map((contract) => {
				const start = this.formatDateForDisplay(contract.contract_start ?? null);
				const end = this.formatDateForDisplay(contract.contract_end ?? null);
				const period = start && end && start !== end ? `${start}–${end}` : (end ?? start ?? 'ohne Vertragszeitraum');
				const label = String(contract.label ?? '').trim();
				return label ? `${label} (${period})` : `Vertrag ${contract.contract_id} (${period})`;
			})
			.join(', ');
	}

	private normalizeContractLink(value: string | null | undefined): string | null {
		if (!value) {
			return null;
		}

		const trimmed = String(value).trim();
		return trimmed !== '' ? trimmed : null;
	}

	documentCategory(doc: ContractDocumentItem): 'A' | 'B' {
		if (doc.category === 'A' || doc.category === 'B') {
			return doc.category;
		}
		const type = (doc.type ?? '').toLowerCase();
		return this.categoryADocumentTypes.includes(type) ? 'A' : 'B';
	}

	getReviewStatusLabel(doc: ContractDocumentItem): string {
		const status = this.documentReviewStatus(doc);
		if (status === 'GEPRUEFT') {
			return 'Geprüft';
		}
		if (status === 'NACHGEFORDERT') {
			return 'Neu angefragt';
		}
		return 'Ungeprüft';
	}

	getReviewStatusClass(doc: ContractDocumentItem): string {
		const status = (doc.review_status ?? 'UNGEPRUEFT').toLowerCase();
		return `review-status-${status}`;
	}

	loadDocuments(force = false): void {
		if (!this.shouldUseDocumentApi || !this.hasCredentials || !this.documentType) {
			return;
		}
		if (this.isLoading) {
			if (force) {
				this.pendingReloadAfterCurrentLoad = true;
			}
			return;
		}

		const requestToken = ++this.loadRequestToken;
		this.errorMessage = null;
		this.isLoading = true;

		const url = this.apiUrl('list');
		const payload = this.buildPayload({
			type: this.documentType
		});
		let context = new HttpContext();
		if (this.isBackendDocumentsApp) {
			context = context.set(SPINNER_SILENT, true);
		}

		const request = this.documentContextMode === 'person'
			? this.httpClient.post<any>(url, payload, { context })
			: this.contractDocumentListCoordinator.loadType(url, payload, this.documentType, context);

		request
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				timeout({ first: 20000 }),
					finalize(() => {
						if (requestToken !== this.loadRequestToken) {
							return;
						}
						this.isLoading = false;
						if (this.pendingReloadAfterCurrentLoad) {
							this.pendingReloadAfterCurrentLoad = false;
							this.loadDocuments();
						}
					})
				)
				.subscribe({
					next: (response) => {
						if (requestToken !== this.loadRequestToken) {
							return;
						}
						const items = Array.isArray(response?.payload) ? (response.payload as ContractDocumentItem[]) : [];
						this.contractDocumentSelectionControl.setValue(null, { emitEvent: false });
						this.allDocuments = items;
						this.lorenzFormControl.payload.documentUploadedItems = items;
						this.applyPartnerHiddenDocumentState();
						this.consumePartnerUploadFeedback();
						this.syncRevalidationBaseline();
						this.evaluateRevalidation();
					},
				error: (error) => {
					if (requestToken !== this.loadRequestToken) {
						return;
					}
					this.allDocuments = [];
					const errorPayload = error?.error?.payload ?? error?.error?.message;
					this.errorMessage = error?.name === 'TimeoutError'
						? 'Dokumente konnten nicht rechtzeitig geladen werden.'
						: (errorPayload || 'Dokumente konnten nicht geladen werden.');
					this.updateControlValue();
				}
			});
	}

	onFileSelected(event: Event): void {
		const input = event.target as HTMLInputElement;
		if (!input.files || input.files.length === 0) {
			return;
		}

		const file = input.files[0];
		input.value = '';
		if (this.isBlocked || !this.allowUpload || this.hasFrozenReviewedDocument) {
			return;
		}
		if (file.size > this.maxTotalUploadSizeKb * 1024) {
			this.errorMessage = `Die ausgewählten Dateien dürfen zusammen maximal ${this.maxTotalUploadSizeLabel} groß sein.`;
			return;
		}

		if (this.shouldRequireDeletingExistingDocumentBeforeUpload()) {
			this.errorMessage = 'Bitte vorhandenes Dokument zuerst löschen.';
			return;
		}

		if (this.isMultiPageEnabled) {
			this.collectMultiPageFile(file);
			return;
		}

		this.queuedFile = file;
		this.errorMessage = null;
		this.revalidationMessage = null;
		this.resetPartnerUploadFeedback();
		this.clearSuccessMessage();
	}

	triggerUpload(): void {
		if (!this.queuedFile) {
			if (this.isMultiPageEnabled && this.hasBufferedMultiPageFiles) {
				this.finishMultiPageUpload();
			}
			return;
		}
		if (!this.hasCredentials || !this.documentType) {
			return;
		}
		if (this.isBlocked || !this.allowUpload || this.shouldRequireDeletingExistingDocumentBeforeUpload() || this.hasFrozenReviewedDocument) {
			return;
		}
		if (this.expiryRequired && !this.expiresAtControl.value) {
			this.errorMessage = 'Ablaufdatum fehlt.';
			this.lorenzFormControl.markAsTouched();
			this.lorenzFormControl.updateValueAndValidity();
			return;
		}
		if (this.isSelectedExpiryInvalidForContractStart) {
			this.errorMessage = this.contractValidityInvalidHint;
			this.lorenzFormControl.markAsTouched();
			this.lorenzFormControl.updateValueAndValidity();
			return;
		}

		const file = this.queuedFile;
		if (this.isMultiPageEnabled) {
			this.collectMultiPageFile(file);
			return;
		}

		if (this.deferUpload) {
			this.queueDocument(file);
		} else {
			this.uploadDocument(file);
		}
	}

	private collectMultiPageFile(file: File): void {
		if (this.pendingFilesBuffer.length >= this.maxUploadFiles) {
			this.errorMessage = `Es können höchstens ${this.maxUploadFiles} Dateien zusammengeführt werden.`;
			this.queuedFile = null;
			return;
		}

		const combinedSize = this.pendingFilesBuffer.reduce((total, pendingFile) => total + pendingFile.size, 0) + file.size;
		if (combinedSize > this.maxTotalUploadSizeKb * 1024) {
			this.errorMessage = `Die ausgewählten Dateien dürfen zusammen maximal ${this.maxTotalUploadSizeLabel} groß sein.`;
			this.queuedFile = null;
			return;
		}

		this.errorMessage = null;
		this.revalidationMessage = null;
		this.resetPartnerUploadFeedback();
		this.clearSuccessMessage();

		this.pendingFilesBuffer.push(file);
		this.queuedFile = null;
		if (this.pendingFilesBuffer.length >= this.maxUploadFiles) {
			this.successMessage = `Maximal ${this.maxUploadFiles} Dateien ausgewählt. Bitte den Upload starten.`;
			return;
		}
		this.askForAdditionalPage();
	}

	private askForAdditionalPage(): void {
		this.dialog
			.open(LibSharedMsgboxComponent, {
				disableClose: true,
				data: {
					title: 'Weitere Seite hochladen',
					message: '<p>Willst du noch eine weitere Seite des Dokuments hochladen?</p>',
					buttons: [MsgboxEnum.JA, MsgboxEnum.NEIN]
				}
			})
			.afterClosed()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((decision: MsgboxEnum | undefined) => {
				if (decision === MsgboxEnum.JA) {
					this.openFileSelector();
					return;
				}
				if (decision === MsgboxEnum.NEIN) {
					this.finishMultiPageUpload();
				}
			});
	}

	private finishMultiPageUpload(): boolean {
		if (!this.pendingFilesBuffer.length) {
			return true;
		}
		if (!this.canFinalizeSelectedUpload()) {
			return false;
		}

		const files = [...this.pendingFilesBuffer];

		if (this.deferUpload) {
			return this.queueDocumentFiles(files);
		} else {
			this.uploadDocumentFiles(files);
			return true;
		}
	}

	private canFinalizeSelectedUpload(): boolean {
		if (!this.hasCredentials || !this.documentType) {
			this.errorMessage = 'Dokument konnte nicht vorbereitet werden. Bitte lade die Seite neu.';
			return false;
		}
		if (this.isBlocked || !this.allowUpload || this.shouldRequireDeletingExistingDocumentBeforeUpload() || this.hasFrozenReviewedDocument) {
			return false;
		}
		if (this.expiryRequired && !this.expiresAtControl.value) {
			this.errorMessage = 'Ablaufdatum fehlt.';
			this.lorenzFormControl.markAsTouched();
			this.lorenzFormControl.updateValueAndValidity();
			return false;
		}
		if (this.isSelectedExpiryInvalidForContractStart) {
			this.errorMessage = this.contractValidityInvalidHint;
			this.lorenzFormControl.markAsTouched();
			this.lorenzFormControl.updateValueAndValidity();
			return false;
		}

		return true;
	}

	private openFileSelector(): void {
		this.fileInputRef?.nativeElement?.click();
	}

	private formatFileNameList(fileNames: string[]): string {
		const normalized = fileNames
			.map((name) => this.formatDisplayFileName(name))
			.filter((name) => name !== '');

		if (normalized.length === 0) {
			return '';
		}

		const visibleNames = normalized.slice(0, 3);
		if (normalized.length <= 3) {
			return visibleNames.join(', ');
		}

		return `${visibleNames.join(', ')} +${normalized.length - 3} weitere`;
	}

	formatDisplayFileName(name: string | null | undefined): string {
		const normalized = String(name ?? '').trim();
		if (normalized === '') {
			return '';
		}

		const lastDotIndex = normalized.lastIndexOf('.');
		if (lastDotIndex <= 0) {
			return this.capitalizeFilenameBase(normalized);
		}

		const baseName = normalized.slice(0, lastDotIndex);
		const extension = normalized.slice(lastDotIndex);
		return `${this.capitalizeFilenameBase(baseName)}${extension}`;
	}

	private capitalizeFilenameBase(value: string): string {
		if (!value) {
			return value;
		}

		return value.charAt(0).toUpperCase() + value.slice(1);
	}

	private queueDocument(file: File): void {
		if (!this.hasCredentials || !this.documentType) {
			return;
		}

		if (this.isBlocked || !this.allowUpload || this.hasFrozenReviewedDocument) {
			return;
		}

		if (this.shouldRequireDeletingExistingDocumentBeforeUpload()) {
			this.errorMessage = 'Bitte vorhandenes Dokument zuerst löschen.';
			return;
		}

		if (this.expiryRequired && !this.expiresAtControl.value) {
			this.errorMessage = 'Ablaufdatum fehlt.';
			this.lorenzFormControl.markAsTouched();
			this.lorenzFormControl.updateValueAndValidity();
			return;
		}
		if (this.isSelectedExpiryInvalidForContractStart) {
			this.errorMessage = this.contractValidityInvalidHint;
			this.lorenzFormControl.markAsTouched();
			this.lorenzFormControl.updateValueAndValidity();
			return;
		}

		this.errorMessage = null;
		this.lorenzFormControl.payload.documentUploadErrorMessage = null;
		this.revalidationMessage = null;
		this.clearSuccessMessage();

		this.lorenzFormControl.payload.documentPendingFile = file;
		this.lorenzFormControl.payload.documentPendingFiles = null;
		this.lorenzFormControl.payload.documentPendingExpiresAt = this.expiresAtControl.value ?? null;
		this.pendingFilesBuffer = [];
		this.queuedFile = null;
		this.expiresAtControl.reset();

		this.needsRevalidation = false;
		this.setBaselineFromCurrentValues();
		this.updateControlValue();
		this.successMessage = 'Dokument zum Upload vorgemerkt.';
		this.scheduleSuccessClear(3000);
	}

	private queueDocumentFiles(files: File[]): boolean {
		if (!this.hasCredentials || !this.documentType) {
			return false;
		}

		if (this.isBlocked || !this.allowUpload || this.hasFrozenReviewedDocument) {
			return false;
		}

		if (this.shouldRequireDeletingExistingDocumentBeforeUpload()) {
			this.errorMessage = 'Bitte vorhandenes Dokument zuerst löschen.';
			return false;
		}

		if (this.expiryRequired && !this.expiresAtControl.value) {
			this.errorMessage = 'Ablaufdatum fehlt.';
			this.lorenzFormControl.markAsTouched();
			this.lorenzFormControl.updateValueAndValidity();
			return false;
		}
		if (this.isSelectedExpiryInvalidForContractStart) {
			this.errorMessage = this.contractValidityInvalidHint;
			this.lorenzFormControl.markAsTouched();
			this.lorenzFormControl.updateValueAndValidity();
			return false;
		}

		this.errorMessage = null;
		this.lorenzFormControl.payload.documentUploadErrorMessage = null;
		this.revalidationMessage = null;
		this.clearSuccessMessage();

		this.lorenzFormControl.payload.documentPendingFile = null;
		this.lorenzFormControl.payload.documentPendingFiles = files;
		this.lorenzFormControl.payload.documentPendingExpiresAt = this.expiresAtControl.value ?? null;
		this.pendingFilesBuffer = [];
		this.queuedFile = null;
		this.expiresAtControl.reset();

		this.needsRevalidation = false;
		this.setBaselineFromCurrentValues();
		this.updateControlValue();
		this.successMessage = 'Dokumente zum Upload vorgemerkt.';
		this.scheduleSuccessClear(3000);
		return true;
	}

	uploadDocument(file: File): void {
		if (!this.hasCredentials || !this.documentType) {
			return;
		}

		if (this.isBlocked || !this.allowUpload || this.hasFrozenReviewedDocument) {
			return;
		}

		if (this.shouldRequireDeletingExistingDocumentBeforeUpload()) {
			this.errorMessage = 'Bitte vorhandenes Dokument zuerst löschen.';
			return;
		}

		if (this.expiryRequired && !this.expiresAtControl.value) {
			this.errorMessage = 'Ablaufdatum fehlt.';
			this.lorenzFormControl.markAsTouched();
			this.lorenzFormControl.updateValueAndValidity();
			return;
		}
		if (this.isSelectedExpiryInvalidForContractStart) {
			this.errorMessage = this.contractValidityInvalidHint;
			this.lorenzFormControl.markAsTouched();
			this.lorenzFormControl.updateValueAndValidity();
			return;
		}

		this.errorMessage = null;
		this.lorenzFormControl.payload.documentUploadErrorMessage = null;
		this.clearSuccessMessage();
		this.revalidationMessage = null;
		this.isUploading = true;

		const url = this.apiUrl('upload');
		const formData = this.buildUploadFormData(file);

		const context = new HttpContext().set(SPINNER_MESSAGE, 'Dokument wird hochgeladen');

		this.httpClient
			.post<any>(url, formData, { context })
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: () => {
					this.expiresAtControl.reset();
					this.queuedFile = null;
					this.pendingFilesBuffer = [];
					this.lorenzFormControl.payload.documentPendingFiles = null;
					if (!this.isContractApp) {
						this.successMessage = 'Erfolgreich Hochgeladen!';
						this.scheduleSuccessClear();
					}
						this.needsRevalidation = false;
						this.revalidationMessage = null;
						this.setBaselineFromCurrentValues();
						this.loadDocuments(true);
					},
				error: (error) => {
					const errorPayload = error?.error?.payload ?? error?.error?.message;
					this.errorMessage = errorPayload || 'Dokument konnte nicht hochgeladen werden.';
					this.clearSuccessMessage();
					this.isUploading = false;
				},
				complete: () => {
					this.isUploading = false;
				}
			});
	}

	private uploadDocumentFiles(files: File[]): void {
		if (!this.hasCredentials || !this.documentType) {
			return;
		}

		if (this.isBlocked || !this.allowUpload || this.hasFrozenReviewedDocument) {
			return;
		}

		if (this.shouldRequireDeletingExistingDocumentBeforeUpload()) {
			this.errorMessage = 'Bitte vorhandenes Dokument zuerst löschen.';
			return;
		}

		if (this.expiryRequired && !this.expiresAtControl.value) {
			this.errorMessage = 'Ablaufdatum fehlt.';
			this.lorenzFormControl.markAsTouched();
			this.lorenzFormControl.updateValueAndValidity();
			return;
		}
		if (this.isSelectedExpiryInvalidForContractStart) {
			this.errorMessage = this.contractValidityInvalidHint;
			this.lorenzFormControl.markAsTouched();
			this.lorenzFormControl.updateValueAndValidity();
			return;
		}

		this.errorMessage = null;
		this.lorenzFormControl.payload.documentUploadErrorMessage = null;
		this.clearSuccessMessage();
		this.revalidationMessage = null;
		this.isUploading = true;

		const url = this.apiUrl('upload');
		const formData = this.buildUploadFormDataFiles(files);
		const context = new HttpContext().set(SPINNER_MESSAGE, 'Dokument wird hochgeladen');

		this.httpClient
			.post<any>(url, formData, { context })
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: () => {
					this.expiresAtControl.reset();
					this.queuedFile = null;
					this.pendingFilesBuffer = [];
					this.lorenzFormControl.payload.documentPendingFile = null;
					this.lorenzFormControl.payload.documentPendingFiles = null;
					if (!this.isContractApp) {
						this.successMessage = 'Erfolgreich Hochgeladen!';
						this.scheduleSuccessClear();
					}
					this.needsRevalidation = false;
					this.revalidationMessage = null;
					this.setBaselineFromCurrentValues();
					this.loadDocuments(true);
				},
				error: (error) => {
					const errorPayload = error?.error?.payload ?? error?.error?.message;
					this.errorMessage = errorPayload || 'Dokument konnte nicht hochgeladen werden.';
					this.clearSuccessMessage();
					this.isUploading = false;
				},
				complete: () => {
					this.isUploading = false;
				}
			});
	}

	clearPendingDocument(): void {
		this.resetPartnerUploadFeedback();
		this.lorenzFormControl.payload.documentUploadErrorMessage = null;
		this.lorenzFormControl.payload.documentPendingFile = null;
		this.lorenzFormControl.payload.documentPendingFiles = null;
		this.lorenzFormControl.payload.documentPendingExpiresAt = null;
		this.pendingFilesBuffer = [];
		this.queuedFile = null;
		this.clearSuccessMessage();
		this.successMessage = null;
		this.updateControlValue();
	}

	deleteDocument(documentId: number, doc?: ContractDocumentItem): void {
		if (!this.hasCredentials || (doc && !this.canDeleteDocument(doc)) || (!doc && (this.isBlocked || !this.allowDelete))) {
			return;
		}

		const isManagementDelete = this.isBackendDocumentsApp;
		const title = isManagementDelete
			? 'Löschung im Dokumentenmanagement bestätigen'
			: 'Dokument löschen';
		const message = isManagementDelete
			? '<p>Das Dokument wird aus dem Dokumentenmanagement und aus allen nicht eingefrorenen Verträgen entfernt.</p><p>Bereits eingefrorene Verträge behalten ihren Dokument-Snapshot.</p><p>Möchten Sie fortfahren?</p>'
			: this.isBackendContractEditApp
				? '<p>Nur die Zuordnung dieses Dokuments zu diesem Vertrag wird entfernt.</p><p>Das Dokument selbst bleibt im Dokumentenmanagement und bei anderen Verträgen erhalten.</p><p>Möchten Sie fortfahren?</p>'
				: '<p>Das Dokument wird gelöscht.</p><p>Möchten Sie fortfahren?</p>';

		this.dialog
			.open(LibSharedMsgboxComponent, {
				data: {
					title,
					message,
					buttons: [MsgboxEnum.CANCEL, MsgboxEnum.OK]
				}
			})
			.afterClosed()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((decision: MsgboxEnum) => {
				if (decision !== MsgboxEnum.OK) {
					return;
				}
				this.performDeleteRequest(documentId, doc, isManagementDelete);
			});
	}

	restoreDocument(): void {
		if (!this.canRestoreDocument) {
			return;
		}

		const state = this.getPartnerHiddenDocumentState();
		if (!state) {
			return;
		}

		this.clearPendingDocument();
		this.setPartnerHiddenDocumentState({
			...state,
			mode: 'restored'
		});
		this.applyPartnerHiddenDocumentState();
	}

	private performDeleteRequest(documentId: number, doc: ContractDocumentItem | undefined, isManagementDelete: boolean, successMessage?: string): void {
		this.errorMessage = null;

		const url = this.apiUrl('delete');
		const payload = this.buildPayload({
			document_id: documentId,
			management_delete: isManagementDelete
		});

		this.httpClient
			.post<any>(url, payload)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: () => {
					if (successMessage) {
						this.successMessage = successMessage;
						this.scheduleSuccessClear(5000);
					} else if (isManagementDelete) {
						this.successMessage = 'Dokument aus dem Management und allen nicht eingefrorenen Verträgen entfernt.';
						this.scheduleSuccessClear(5000);
					} else if (this.isBackendContractEditApp) {
						this.clearSelectedDocumentIdInForm();
						this.updateDetachedDocumentIdInForm(documentId);
						this.successMessage = 'Dokumentzuordnung für diesen Vertrag entfernt.';
						this.scheduleSuccessClear(5000);
					} else if (this.isContractApp) {
						this.successMessage = 'Dokument gelöscht. Du kannst jetzt ein neues Dokument hochladen.';
						this.scheduleSuccessClear(5000);
					}
					this.removeDocumentFromLocalState(documentId);
					this.loadDocuments(true);
				},
				error: (error) => {
					const errorPayload = error?.error?.payload ?? error?.error?.message;
					this.errorMessage = errorPayload || 'Dokument konnte nicht gelöscht werden.';
				}
			});
	}

	private removeDocumentFromLocalState(documentId: number): void {
		const withoutDeletedDocument = (items: ContractDocumentItem[]) => items.filter((item) => item.id !== documentId);

		this.allDocuments = withoutDeletedDocument(this.allDocuments);
		this.documents = withoutDeletedDocument(this.documents);
		this.expiredDocuments = withoutDeletedDocument(this.expiredDocuments);
		this.lorenzFormControl.payload.documentUploadedItems = withoutDeletedDocument(this.uploadedDocuments);
		this.setPartnerHiddenDocumentState(null);
		this.updateControlValue();
	}

	downloadDocument(doc: ContractDocumentItem): void {
		if (!this.hasCredentials || !this.canDownloadDocument(doc)) {
			return;
		}

		this.errorMessage = null;

		const url = this.apiUrl('download');
		const payload = this.buildPayload({
			document_id: doc.id
		});

		this.httpClient
			.post(url, payload, { responseType: 'blob', observe: 'response' })
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (response: HttpResponse<Blob>) => {
					const blob = response.body;
					if (!blob) {
						this.errorMessage = 'Dokument konnte nicht heruntergeladen werden.';
						return;
					}
					const downloadUrl = URL.createObjectURL(blob);
					const link = document.createElement('a');
					link.href = downloadUrl;
					link.download = this.resolveDownloadFilename(response, doc);
					link.click();
					URL.revokeObjectURL(downloadUrl);
					if (!doc.downloaded_at) {
						doc.downloaded_at = this.formatDateTime(new Date());
					}
					if (this.canReviewDocuments && !doc.review_gate_seen_at) {
						doc.review_gate_seen_at = this.formatDateTime(new Date());
					}
				},
				error: (error) => {
					const errorPayload = error?.error?.payload ?? error?.error?.message;
					this.errorMessage = errorPayload || 'Dokument konnte nicht heruntergeladen werden.';
				}
				});
	}

	previewDocument(doc: ContractDocumentItem): void {
		if (!this.hasCredentials || !this.canPreviewDocument(doc)) {
			return;
		}

		this.errorMessage = null;

		const url = this.apiUrl('preview');
		const payload = this.buildPayload({
			document_id: doc.id
		});

		this.httpClient
			.post(url, payload, { responseType: 'blob' })
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (blob) => {
					const previewUrl = URL.createObjectURL(blob);
					const link = document.createElement('a');
					link.href = previewUrl;
					link.target = '_blank';
					link.rel = 'noopener';
					link.click();
					setTimeout(() => URL.revokeObjectURL(previewUrl), 1000);
					if (this.canReviewDocuments && !doc.review_gate_seen_at) {
						doc.review_gate_seen_at = this.formatDateTime(new Date());
					}
				},
				error: (error) => {
					const errorPayload = error?.error?.payload ?? error?.error?.message;
					this.errorMessage = errorPayload || 'Dokument konnte nicht angezeigt werden.';
				}
			});
	}

	previewPendingDocument(): void {
		if (!this.allowPreview) {
			return;
		}

		const multiFiles = this.pendingFiles;
		const file = multiFiles.length > 0 ? multiFiles[0] : this.lorenzFormControl.payload?.documentPendingFile;
		if (!file) {
			return;
		}

		this.errorMessage = null;

		const previewUrl = URL.createObjectURL(file);
		const link = document.createElement('a');
		link.href = previewUrl;
		link.target = '_blank';
		link.rel = 'noopener';
		link.click();
		setTimeout(() => URL.revokeObjectURL(previewUrl), 1000);
	}

	markDocumentAsReviewed(doc: ContractDocumentItem): void {
		this.updateDocumentReviewStatus(doc, 'GEPRUEFT');
	}

	acceptDocument(doc: ContractDocumentItem): void {
		if (!this.canAcceptDocument(doc)) {
			return;
		}

		this.errorMessage = null;
		this.clearSuccessMessage();

		const url = this.apiUrl('accept');
		const payload = this.buildPayload({
			document_id: doc.id
		});
		const context = new HttpContext().set(SPINNER_MESSAGE, 'Dokument wird angenommen');

		this.httpClient
			.post<any>(url, payload, { context })
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (response) => {
					if (this.isBackendContractEditApp) {
						if (response?.payload?.is_selected_for_contract === true) {
							this.updateSelectedDocumentIdInForm(doc.id);
						} else {
							this.clearSelectedDocumentIdInForm();
						}
					}
					this.successMessage = this.isBackendContractEditApp
						? (response?.payload?.is_selected_for_contract === true
							? 'Ersatz übernommen. Das neue Dokument wird im Vertrag verwendet und muss noch geprüft werden.'
							: 'Ersatz übernommen. Das neue Dokument wird nach der Prüfung automatisch im Vertrag verwendet.')
						: 'Dokument angenommen. Es wird jetzt für neue Verträge bevorzugt.';
					this.scheduleSuccessClear(5000);
					this.loadDocuments(true);
				},
				error: (error) => {
					const errorPayload = error?.error?.payload ?? error?.error?.message;
					this.errorMessage = errorPayload || 'Dokument konnte nicht angenommen werden.';
				}
				});
	}

	selectContractDocumentFromDropdown(): void {
		const documentId = Number(this.contractDocumentSelectionControl.value ?? 0);
		const document = this.selectableContractDocuments.find((doc) => doc.id === documentId);
		if (!document) {
			this.contractDocumentSelectionControl.setValue(null, { emitEvent: false });
			return;
		}

		this.selectDocumentForContract(document);
	}

	private selectDocumentForContract(doc: ContractDocumentItem): void {
		if (!this.canSelectDocumentForContract(doc) || !this.documentType) {
			this.contractDocumentSelectionControl.setValue(null, { emitEvent: false });
			return;
		}

		this.errorMessage = null;
		this.clearSuccessMessage();
		this.isSelectingContractDocument = true;

		const url = this.apiUrl('select');
		const payload = this.buildPayload({
			document_id: doc.id,
			type: this.documentType
		});
		const context = new HttpContext().set(SPINNER_MESSAGE, 'Dokument wird für Vertrag ausgewählt');

		this.httpClient
			.post<any>(url, payload, { context })
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				finalize(() => this.isSelectingContractDocument = false)
			)
			.subscribe({
				next: () => {
					this.updateSelectedDocumentIdInForm(doc.id);
					this.clearDetachedDocumentIdInForm(doc.id);
					this.contractDocumentSelectionControl.setValue(null, { emitEvent: false });
					this.successMessage = 'Dokument für diesen Vertrag ausgewählt.';
					this.scheduleSuccessClear(4000);
					this.loadDocuments(true);
				},
				error: (error) => {
					this.contractDocumentSelectionControl.setValue(null, { emitEvent: false });
					const errorPayload = error?.error?.payload ?? error?.error?.message;
					this.errorMessage = errorPayload || 'Dokument konnte nicht für den Vertrag ausgewählt werden.';
				}
			});
	}

	resetDocumentSelection(doc: ContractDocumentItem): void {
		if (!this.canResetDocumentSelection(doc) || !this.documentType) {
			return;
		}

		this.errorMessage = null;
		this.clearSuccessMessage();

		const url = this.apiUrl('clear-selection');
		const payload = this.buildPayload({ type: this.documentType });
		const context = new HttpContext().set(SPINNER_MESSAGE, 'Automatische Dokumentauswahl wird wiederhergestellt');

		this.httpClient
			.post<any>(url, payload, { context })
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: () => {
					this.clearSelectedDocumentIdInForm();
					this.successMessage = 'Automatische Dokumentauswahl wiederhergestellt.';
					this.scheduleSuccessClear(4000);
					this.loadDocuments(true);
				},
				error: (error) => {
					const errorPayload = error?.error?.payload ?? error?.error?.message;
					this.errorMessage = errorPayload || 'Automatische Dokumentauswahl konnte nicht wiederhergestellt werden.';
				}
			});
	}

	setCurrentDocument(doc: ContractDocumentItem): void {
		if (!this.canSetCurrentDocument(doc)) {
			return;
		}

		this.errorMessage = null;
		this.clearSuccessMessage();

		const url = this.apiUrl('set-current');
		const payload = this.buildPayload({ document_id: doc.id });
		const context = new HttpContext().set(SPINNER_MESSAGE, 'Aktuelles Dokument wird gesetzt');

		this.httpClient
			.post<any>(url, payload, { context })
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: () => {
					this.successMessage = 'Dokument als aktuell festgelegt.';
					this.scheduleSuccessClear(4000);
					this.loadDocuments(true);
				},
				error: (error) => {
					const errorPayload = error?.error?.payload ?? error?.error?.message;
					this.errorMessage = errorPayload || 'Dokument konnte nicht als aktuell festgelegt werden.';
				}
			});
	}

	rejectDocument(doc: ContractDocumentItem): void {
		if (!this.canRejectDocument(doc)) {
			return;
		}

		this.dialog
			.open(LibSharedMsgboxComponent, {
				data: {
					title: 'Dokument ablehnen',
					message: '<p>Das neue Dokument wird abgelehnt und entfernt.</p><p>Falls ein vorheriges gültiges Dokument vorhanden ist, wird es wieder als aktueller Stand verwendet.</p>',
					buttons: [MsgboxEnum.CANCEL, MsgboxEnum.OK]
				}
			})
			.afterClosed()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((decision: MsgboxEnum) => {
				if (decision !== MsgboxEnum.OK) {
					return;
				}

				this.performDeleteRequest(
					doc.id,
					doc,
					this.isBackendDocumentsApp,
					'Dokument abgelehnt. Der vorherige Stand wurde wiederhergestellt, sofern vorhanden.'
				);
			});
	}

	startDocumentExpiryEdit(doc: ContractDocumentItem): void {
		if (!this.canEditDocumentExpiry(doc)) {
			return;
		}

		this.editingExpiryDocumentKey = this.getDocumentIdentity(doc);
		this.expiryEditControl.setValue(doc.expires_at ?? null);
		this.errorMessage = null;
		this.clearSuccessMessage();
	}

	cancelDocumentExpiryEdit(): void {
		this.editingExpiryDocumentKey = null;
		this.expiryEditControl.reset();
		this.isSavingExpiry = false;
	}

	saveDocumentExpiry(doc: ContractDocumentItem): void {
		if (!this.hasCredentials || !this.canEditDocumentExpiry(doc) || !this.isEditingDocumentExpiry(doc)) {
			return;
		}

		const expiresAt = (this.expiryEditControl.value ?? '').trim();
		if (!expiresAt) {
			this.errorMessage = 'Ablaufdatum fehlt.';
			return;
		}

		this.errorMessage = null;
		this.clearSuccessMessage();
		this.isSavingExpiry = true;

		const url = this.apiUrl('update-expiry');
		const payload = this.buildPayload({
			document_id: doc.id,
			expires_at: expiresAt
		});
		const context = new HttpContext().set(SPINNER_MESSAGE, 'Ablaufdatum wird aktualisiert');

		this.httpClient
			.post<any>(url, payload, { context })
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (response) => {
					const updated = response?.payload ?? {};
					doc.expires_at = (updated.expires_at as string | null | undefined) ?? expiresAt;
					doc.is_expired = false;
					doc.is_invalid_for_contract_start = false;
					doc.invalid_for_contract_start_reason = null;
					this.cancelDocumentExpiryEdit();
					this.successMessage = 'Ablaufdatum aktualisiert. Dokument muss erneut geprüft werden.';
					this.scheduleSuccessClear(4000);
					this.loadDocuments(true);
				},
				error: (error) => {
					const errorPayload = error?.error?.payload ?? error?.error?.message;
					this.errorMessage = errorPayload || 'Ablaufdatum konnte nicht aktualisiert werden.';
					this.isSavingExpiry = false;
				},
				complete: () => {
					this.isSavingExpiry = false;
				}
			});
	}

	private updateDocumentReviewStatus(doc: ContractDocumentItem, reviewStatus: ContractDocumentReviewStatus): void {
		if (!this.hasCredentials || !this.canMarkReviewStatus(doc)) {
			return;
		}

		this.errorMessage = null;
		this.successMessage = null;

		const url = this.apiUrl('review-status');
		const payload = this.buildPayload({
			document_id: doc.id,
			review_status: reviewStatus
		});

		const context = new HttpContext().set(SPINNER_MESSAGE, 'Dokumentenstatus wird aktualisiert');

		this.httpClient
			.post<any>(url, payload, { context })
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (response) => {
					const updated = response?.payload ?? {};
					doc.review_status = (updated.review_status as ContractDocumentReviewStatus) ?? reviewStatus;
					doc.review_gate_seen_at = updated.review_gate_seen_at ?? doc.review_gate_seen_at ?? this.formatDateTime(new Date());
					doc.reviewed_at = updated.reviewed_at ?? null;
					doc.requested_at = updated.requested_at ?? null;
					doc.requested_reason = updated.requested_reason ?? null;
					const healthCertificateExpiresAt = this.formatDateForDisplay(updated.health_certificate_expires_at ?? null);
					this.successMessage = healthCertificateExpiresAt
						? `Auffrischung geprüft. Das Gesundheitszeugnis ist jetzt bis ${healthCertificateExpiresAt} gültig.`
						: reviewStatus === 'GEPRUEFT'
							? 'Dokument als geprüft markiert.'
							: 'Dokument als neu angefragt markiert.';
					if (healthCertificateExpiresAt) {
						window.dispatchEvent(new CustomEvent('lorenz-health-certificate-refreshed'));
					}
					this.scheduleSuccessClear(4000);
					this.loadDocuments(true);
				},
				error: (error) => {
					const errorPayload = error?.error?.payload ?? error?.error?.message;
					this.errorMessage = errorPayload || 'Dokumentenstatus konnte nicht aktualisiert werden.';
				}
			});
	}

	private clearPendingUploadSelection(): void {
		this.lorenzFormControl.payload.documentPendingFile = null;
		this.lorenzFormControl.payload.documentPendingFiles = null;
		this.lorenzFormControl.payload.documentPendingExpiresAt = null;
		this.pendingFilesBuffer = [];
		this.queuedFile = null;
		this.expiresAtControl.reset();
		this.updateControlValue();
	}

	private canManageDocumentInCurrentState(doc: ContractDocumentItem): boolean {
		if (!this.isBlocked) {
			return true;
		}
		if (!(this.allowSignedCategoryBManagement && this.documentCategory(doc) === 'B')) {
			return false;
		}
		return !this.isDocumentFrozenInCurrentContract(doc);
	}

	private shouldRequireDeletingExistingDocumentBeforeUpload(): boolean {
		if (this.isContractApp) {
			return this.hasExistingPartnerDocument;
		}

		return this.hasValidDocument && !this.isBackendContractEditApp;
	}

	private updateSelectedDocumentIdInForm(documentId: number): void {
		const rootGroup = (this.lorenzFormControl?.getRoot?.() as LorenzFormGroup | null) ?? this.lorenzFormGroup ?? null;
		const selectedControl = rootGroup?.get('_lorenz.selected_document_ids') as LorenzFormControl<Record<string, number> | null> | null;
		if (!selectedControl || !this.documentType) {
			return;
		}

		selectedControl.patchValue(
			{
				...(selectedControl.value ?? {}),
				[this.documentType]: documentId
			},
			{ emitEvent: false }
		);
		selectedControl.updateValueAndValidity({ emitEvent: false });
	}

	private clearSelectedDocumentIdInForm(): void {
		const rootGroup = (this.lorenzFormControl?.getRoot?.() as LorenzFormGroup | null) ?? this.lorenzFormGroup ?? null;
		const selectedControl = rootGroup?.get('_lorenz.selected_document_ids') as LorenzFormControl<Record<string, number> | null> | null;
		if (!selectedControl || !this.documentType) {
			return;
		}

		const selectedDocumentIds = { ...(selectedControl.value ?? {}) };
		delete selectedDocumentIds[this.documentType];
		selectedControl.patchValue(Object.keys(selectedDocumentIds).length > 0 ? selectedDocumentIds : null, { emitEvent: false });
		selectedControl.updateValueAndValidity({ emitEvent: false });
	}

	private updateDetachedDocumentIdInForm(documentId: number): void {
		const rootGroup = (this.lorenzFormControl?.getRoot?.() as LorenzFormGroup | null) ?? this.lorenzFormGroup ?? null;
		const detachedControl = rootGroup?.get('_lorenz.detached_document_ids') as LorenzFormControl<Record<string, number> | null> | null;
		if (!detachedControl || !this.documentType) {
			return;
		}

		detachedControl.patchValue(
			{
				...(detachedControl.value ?? {}),
				[this.documentType]: documentId
			},
			{ emitEvent: false }
		);
		detachedControl.updateValueAndValidity({ emitEvent: false });
	}

	private clearDetachedDocumentIdInForm(documentId?: number): void {
		const rootGroup = (this.lorenzFormControl?.getRoot?.() as LorenzFormGroup | null) ?? this.lorenzFormGroup ?? null;
		const detachedControl = rootGroup?.get('_lorenz.detached_document_ids') as LorenzFormControl<Record<string, number> | null> | null;
		if (!detachedControl || !this.documentType) {
			return;
		}

		const detachedDocumentIds = { ...(detachedControl.value ?? {}) };
		if (documentId !== undefined && Number(detachedDocumentIds[this.documentType] ?? 0) !== documentId) {
			return;
		}
		delete detachedDocumentIds[this.documentType];
		detachedControl.patchValue(Object.keys(detachedDocumentIds).length > 0 ? detachedDocumentIds : null, { emitEvent: false });
		detachedControl.updateValueAndValidity({ emitEvent: false });
	}

	private isReviewedDeleteAllowedInCurrentStatus(): boolean {
		return [ProcessStatusEnum.NEW, ProcessStatusEnum.REVIEW].includes(this.currentProcessStatus ?? ProcessStatusEnum.UNKNOWN);
	}

	private getDocumentIdentity(doc: ContractDocumentItem): string {
		return String(doc.id);
	}

	private filterDocumentsForCurrentApp(items: ContractDocumentItem[]): ContractDocumentItem[] {
		if (this.isContractApp) {
			return items.filter((doc) =>
				doc.is_selected_for_contract === true
				|| this.isPendingPartnerUpload(doc)
				|| (this.isFileAvailable(doc) && !doc.is_expired && !this.isInvalidForContractStart(doc))
			);
		}

		return items;
	}

	private hideDocumentForPartner(doc: ContractDocumentItem): void {
		this.errorMessage = null;
		this.setPartnerHiddenDocumentState({
			mode: 'hidden',
			document: this.cloneValue(doc)
		});
		this.applyPartnerHiddenDocumentState();
	}

	private applyPartnerHiddenDocumentState(): void {
		const state = this.getPartnerHiddenDocumentState();
		let visibleItems = this.filterDocumentsForCurrentApp(this.allDocuments);

		if (this.isContractApp && state) {
			const hiddenId = Number(state.document?.id ?? 0);
			if (state.mode === 'hidden') {
				visibleItems = visibleItems.filter((item) => item.id !== hiddenId);
			} else if (state.mode === 'restored') {
				const hasRestoredItem = visibleItems.some((item) => item.id === hiddenId);
				if (!hasRestoredItem) {
					const syntheticItem = this.cloneValue(state.document) as ContractDocumentItem;
					syntheticItem.file_available = false;
					visibleItems = [syntheticItem, ...visibleItems];
				}
			}
		}

		this.documents = visibleItems.filter((item) => !item.is_expired);
		this.expiredDocuments = visibleItems.filter((item) => item.is_expired);
		this.updateControlValue();
	}

	private applyUploadedItemsFromSave(items: ContractDocumentItem[]): void {
		const uploadedItems = Array.isArray(items) ? items : [];
		this.allDocuments = uploadedItems;
		this.lorenzFormControl.payload.documentUploadedItems = uploadedItems;
		this.applyPartnerHiddenDocumentState();
		this.consumePartnerUploadFeedback();
		this.syncRevalidationBaseline();
		this.evaluateRevalidation();
	}

	private getPartnerHiddenDocumentStorageKey(): string | null {
		if (!this.isContractApp || !this.documentType) {
			return null;
		}

		const code = String(this.lorenzFormControl.payload?.code ?? '').trim();
		return code !== '' ? `partner-hidden-document:${code}:${this.documentType}` : null;
	}

	private getPartnerUploadFeedbackStorageKey(): string | null {
		if (!this.isContractApp || !this.documentType) {
			return null;
		}

		const code = String(this.lorenzFormControl.payload?.code ?? '').trim();
		return code !== '' ? `partner-upload-feedback:${code}:${this.documentType}` : null;
	}

	private getPartnerHiddenDocumentState(): PartnerHiddenDocumentState | null {
		const key = this.getPartnerHiddenDocumentStorageKey();
		if (!key) {
			return null;
		}

		try {
			const raw = window.sessionStorage.getItem(key);
			if (!raw) {
				return null;
			}

			const parsed = JSON.parse(raw) as PartnerHiddenDocumentState | null;
			return parsed?.document && parsed?.mode ? parsed : null;
		} catch {
			return null;
		}
	}

	private setPartnerHiddenDocumentState(state: PartnerHiddenDocumentState | null): void {
		const key = this.getPartnerHiddenDocumentStorageKey();
		if (!key) {
			return;
		}

		try {
			if (!state) {
				window.sessionStorage.removeItem(key);
				return;
			}

			window.sessionStorage.setItem(key, JSON.stringify(state));
		} catch {
			// Ignore storage failures.
		}
	}

	private consumePartnerUploadFeedback(): void {
		const payloadMessage = this.lorenzFormControl.payload?.documentUploadStatusMessage ?? null;
		if (payloadMessage) {
			this.successMessage = payloadMessage;
			this.lorenzFormControl.payload.documentUploadStatusMessage = null;
			const key = this.getPartnerUploadFeedbackStorageKey();
			if (key) {
				try {
					window.sessionStorage.removeItem(key);
				} catch {
					// Ignore storage failures.
				}
			}
			this.scheduleSuccessClear(5000);
			return;
		}

		const key = this.getPartnerUploadFeedbackStorageKey();
		if (!key) {
			return;
		}

		try {
			const raw = window.sessionStorage.getItem(key);
			if (!raw) {
				return;
			}

			const feedback = JSON.parse(raw) as PartnerUploadFeedbackState | null;
			if (!feedback?.message) {
				window.sessionStorage.removeItem(key);
				return;
			}

			this.successMessage = feedback.message;
			window.sessionStorage.removeItem(key);
			this.scheduleSuccessClear(5000);
		} catch {
			// Ignore storage failures.
		}
	}

	private resetPartnerUploadFeedback(): void {
		this.lorenzFormControl.payload.documentUploadStatusMessage = null;
		const key = this.getPartnerUploadFeedbackStorageKey();
		if (!key) {
			return;
		}

		try {
			window.sessionStorage.removeItem(key);
		} catch {
			// Ignore storage failures.
		}
	}

	private formatDateTime(date: Date): string {
		const pad = (value: number) => String(value).padStart(2, '0');
		return [
			date.getFullYear(),
			pad(date.getMonth() + 1),
			pad(date.getDate())
		].join('-') + ' ' + [pad(date.getHours()), pad(date.getMinutes()), pad(date.getSeconds())].join(':');
	}

	private isFileAvailable(doc: ContractDocumentItem): boolean {
		return doc.file_available !== false;
	}

	private isDocumentFrozenInCurrentContract(doc: ContractDocumentItem): boolean {
		if (this.isBackendContractEditApp && doc.is_selected_for_contract === false) {
			return false;
		}

		if (![
			ProcessStatusEnum.YOUSIGN_LORENZ,
			ProcessStatusEnum.YOUSIGN_PARTNER,
			ProcessStatusEnum.SIGNED,
			ProcessStatusEnum.CANCELED,
			ProcessStatusEnum.DECLINED
		].includes(this.currentProcessStatus ?? ProcessStatusEnum.UNKNOWN)) {
			return false;
		}

		if (this.documentReviewStatus(doc) !== 'GEPRUEFT') {
			return false;
		}

		const expiresAt = this.parseDateOnly(doc.expires_at);
		if (!expiresAt) {
			return true;
		}

		if (!this.isPastDate(expiresAt)) {
			return true;
		}

		const reviewedAt = this.parseDateTime(doc.reviewed_at);
		if (!reviewedAt) {
			return false;
		}

		return reviewedAt.getTime() <= this.endOfDay(expiresAt).getTime();
	}

	private parseDateOnly(value: string | null | undefined): Date | null {
		if (!value) {
			return null;
		}

		const parsed = new Date(`${value}T00:00:00`);
		return Number.isNaN(parsed.getTime()) ? null : parsed;
	}

	private parseDateTime(value: string | null | undefined): Date | null {
		if (!value) {
			return null;
		}

		const normalized = value.includes('T') ? value : value.replace(' ', 'T');
		const parsed = new Date(normalized);
		return Number.isNaN(parsed.getTime()) ? null : parsed;
	}

	private isPastDate(value: Date): boolean {
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		return value.getTime() < today.getTime();
	}

	private endOfDay(value: Date): Date {
		const copy = new Date(value.getTime());
		copy.setHours(23, 59, 59, 999);
		return copy;
	}

	private isDateBeforeContractValidityEnd(value: string | null | undefined): boolean {
		const expiresAt = this.parseDateOnly(value);
		const contractValidityDate = this.parseDateOnly(this.contractValidityDate);
		if (!expiresAt || !contractValidityDate) {
			return false;
		}

		return expiresAt.getTime() < contractValidityDate.getTime();
	}

	private isDateOnOrAfterContractValidityEnd(value: string | null | undefined): boolean {
		const expiresAt = this.parseDateOnly(value);
		const contractValidityDate = this.parseDateOnly(this.contractValidityDate);
		if (!expiresAt || !contractValidityDate) {
			return false;
		}

		return expiresAt.getTime() >= contractValidityDate.getTime();
	}

	private resolveDownloadFilename(response: HttpResponse<Blob>, doc: ContractDocumentItem): string {
		const contentDisposition = response.headers.get('content-disposition') ?? response.headers.get('Content-Disposition') ?? '';
		const filenameStarMatch = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
		if (filenameStarMatch && filenameStarMatch[1]) {
			try {
				return decodeURIComponent(filenameStarMatch[1]).replace(/["']/g, '').trim();
			} catch {
				return filenameStarMatch[1].replace(/["']/g, '').trim();
			}
		}

		const filenameMatch = /filename="?([^";]+)"?/i.exec(contentDisposition);
		if (filenameMatch && filenameMatch[1]) {
			return filenameMatch[1].trim();
		}

		return doc.original_name || 'document';
	}

	private setupRevalidation(): void {
		if (!this.revalidateTriggers.length) {
			return;
		}

		this.revalidateControls = this.revalidateTriggers
			.map((key) => {
				const control = this.resolveTriggerControl(key);
				return control ? { key, control } : null;
			})
			.filter(Boolean) as { key: string; control: LorenzFormControl }[];

		if (!this.revalidateControls.length) {
			return;
		}

		this.setBaselineFromCurrentValues();

		this.revalidateControls.forEach(({ control }) => {
			control.valueChanges
				.pipe(debounceTime(600), takeUntilDestroyed(this.destroyRef))
				.subscribe(() => {
					this.evaluateRevalidation();
					this.updateControlValue();
				});
		});
	}

	private prepareDeferredUploadForSave(): boolean {
		if (!this.deferUpload) {
			return true;
		}
		if (this.hasBufferedMultiPageFiles) {
			return this.finishMultiPageUpload();
		}
		if (this.queuedFile) {
			if (!this.canFinalizeSelectedUpload()) {
				return false;
			}
			const file = this.queuedFile;
			this.queueDocument(file);
			return !this.queuedFile;
		}

		return true;
	}

	private resolveTriggerControl(key: string): LorenzFormControl | null {
		if (!this.lorenzFormGroup) {
			return null;
		}

		let searchRoot = this.lorenzFormGroup;
		let controlPath = key;
		if (key.startsWith('.')) {
			searchRoot = this.lorenzFormControl.parent as LorenzFormGroup;
			controlPath = key.slice(1);
		}

		if (!searchRoot) {
			return null;
		}

		const control = searchRoot.get(controlPath);
		return control instanceof LorenzFormControl ? control : null;
	}

	private setBaselineFromCurrentValues(): void {
		if (!this.revalidateControls.length) {
			return;
		}

		this.revalidateBaseline.clear();
		this.revalidateControls.forEach(({ key, control }) => {
			this.revalidateBaseline.set(key, this.cloneValue(control.value));
		});
		this.baselineInitialized = true;
	}

	private syncRevalidationBaseline(): void {
		const currentId = this.documents[0]?.id ?? null;
		if (!currentId) {
			this.lastDocumentId = null;
			return;
		}

		if (!this.baselineInitialized || this.lastDocumentId !== currentId) {
			this.setBaselineFromCurrentValues();
			this.lastDocumentId = currentId;
			this.needsRevalidation = false;
			this.revalidationMessage = null;
		}
	}

	private evaluateRevalidation(): void {
		if (!this.revalidateControls.length) {
			return;
		}

		if (!this.hasValidDocument) {
			this.needsRevalidation = false;
			this.revalidationMessage = null;
			return;
		}

		const changed = this.revalidateControls.some(({ key, control }) => {
			const baseline = this.revalidateBaseline.get(key);
			return !this.valuesEqual(baseline, control.value);
		});

		this.needsRevalidation = changed;
		this.revalidationMessage = changed ? 'Angaben wurden geändert. Dokument bitte neu hochladen.' : null;
		if (changed) {
			this.lorenzFormControl.markAsTouched();
			this.lorenzFormControl.markAsDirty();
			this.lorenzFormControl.setErrors({ documentOutdated: true });
		}
	}

	private valuesEqual(a: any, b: any): boolean {
		if (a === b) {
			return true;
		}
		return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
	}

	private cloneValue(value: any): any {
		if (value === null || value === undefined) {
			return value;
		}
		try {
			return JSON.parse(JSON.stringify(value));
		} catch {
			return value;
		}
	}

	private updateControlValue(): void {
		if (this.isPersonDocumentsApp) {
			this.lorenzFormControl.patchValue(null, { emitEvent: false });
			this.lorenzFormControl.setErrors(null);
			this.lorenzFormControl.updateValueAndValidity({ emitEvent: false });
			return;
		}

		const validDocuments = this.controlValueDocuments ?? [];
		const hasValidDocuments = validDocuments.length > 0;
		const hasValidOrPending = hasValidDocuments || this.hasValidPendingUpload;
		const valueForControl = hasValidDocuments
			? validDocuments
			: this.hasValidPendingUpload
				? [{ pending: true, expires_at: this.pendingExpiresAt }]
				: null;
		const nextValue = hasValidOrPending ? valueForControl : null;
		const shouldEmitEvent = !this.valuesEqual(this.lorenzFormControl.value, nextValue);
		this.lorenzFormControl.patchValue(nextValue, { emitEvent: shouldEmitEvent });

		if (this.needsRevalidation) {
			this.lorenzFormControl.setErrors({ documentOutdated: true });
			return;
		}

		if (this.lorenzFormControl.isValidationEnabled()) {
			if (hasValidOrPending && !this.needsRevalidation) {
				this.lorenzFormControl.setErrors(null);
			} else if (this.lorenzFormControl.isRequired()) {
				this.lorenzFormControl.setErrors({ required: true });
			}
		}
		this.lorenzFormControl.updateValueAndValidity({ emitEvent: false });
	}

	private scheduleSuccessClear(timeoutMs = 7000): void {
		if (this.successTimeoutId) {
			clearTimeout(this.successTimeoutId);
		}
		this.successTimeoutId = window.setTimeout(() => {
			this.successMessage = null;
			this.successTimeoutId = null;
		}, timeoutMs);
	}

	private clearSuccessMessage(): void {
		if (this.successTimeoutId) {
			clearTimeout(this.successTimeoutId);
			this.successTimeoutId = null;
		}
		this.successMessage = null;
	}

	private initControlContext(): void {
		this.lorenzFormGroup = this.lorenzFormControl.parent as LorenzFormGroup;
		this.lorenzFormControl.payload.displayOptions = this.lorenzFormComponentDisplayOptions;
		this.lorenzFormControl.payload.documentPreparePendingUpload = this.preparePendingUploadForSave;
		this.lorenzFormControl.payload.documentApplyUploadedItems = this.applyUploadedItemsAfterSave;

		// prettier-ignore
		this.hostCssClass = [
			this.lorenzFormControl.getKey(),
			getLorenzFormLayoutEnumString(this.lorenzFormComponentDisplayOptions?.layout ?? null)
		].join(' ');

		if (!this.revalidationInitialized) {
			this.setupRevalidation();
			this.revalidationInitialized = true;
		}

		if (this.isContractApp && (this.currentProcessStatus ?? ProcessStatusEnum.UNKNOWN) !== ProcessStatusEnum.NEW) {
			this.setPartnerHiddenDocumentState(null);
		}

		this.consumePartnerUploadFeedback();
		this.loadDocuments();
	}

	private unregisterPendingUploadPreparation(control: LorenzFormControl | null | undefined): void {
		if (control?.payload?.documentPreparePendingUpload === this.preparePendingUploadForSave) {
			control.payload.documentPreparePendingUpload = undefined;
		}
		if (control?.payload?.documentApplyUploadedItems === this.applyUploadedItemsAfterSave) {
			control.payload.documentApplyUploadedItems = undefined;
		}
	}

	private resetStateForControlChange(): void {
		this.loadRequestToken++;
		this.pendingReloadAfterCurrentLoad = false;
		this.allDocuments = [];
		this.documents = [];
		this.expiredDocuments = [];
		this.errorMessage = null;
		this.revalidationMessage = null;
		this.isLoading = false;
		this.isUploading = false;
		this.isSavingExpiry = false;
		this.isSelectingContractDocument = false;
		this.queuedFile = null;
		this.pendingFilesBuffer = [];
		this.expiresAtControl.reset();
		this.expiryEditControl.reset();
		this.contractDocumentSelectionControl.reset(null, { emitEvent: false });
		this.editingExpiryDocumentKey = null;
		this.needsRevalidation = false;
		this.lastDocumentId = null;
		this.resetPartnerUploadFeedback();
		this.clearSuccessMessage();
	}

}
